import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Sun, Moon, Users, SignOut, User,
  FileText, Table, Presentation, ClipboardText, Palette,
  CaretDown, CaretUp,
  Trash, Envelope, Flag, ArrowRight,
  Briefcase, Storefront, Code, Wrench, Globe, Camera,
  Megaphone, Headset, Book, TreeStructure, ShieldCheck,
  Heartbeat, GraduationCap, Leaf, Coffee, GameController,
  MusicNote, Airplane, Buildings, HandCoins,
  ChartBar, ChartLine, ChartPie,
  Hammer, Scales, CurrencyDollar, Handshake, Receipt, Tag, Package, Truck, Warehouse, Factory,
  Cpu, Desktop, DeviceMobile, Printer, Microphone, Gear, Terminal, Database, Cloud, Robot, Monitor, WifiHigh, HardDrive, Bug,
  Pen, PenNib, Pencil, Stack,
  Heart, FirstAidKit, Stethoscope, Pill, ForkKnife, CookingPot, Wine, BeerStein, Barbell, Bicycle, Football, Basketball, Baseball, Waves, Fish, Dog, Cat, PawPrint,
  Tree, Umbrella, SunHorizon, Tent, Campfire,
  Headphones, PaintBrush, FilmStrip, VideoCamera, Guitar, Scissors,
  House, Star, Lightning, Fire, Smiley, Diamond, Crown, Rocket, Broadcast, Newspaper, Bag,
} from '@phosphor-icons/react';
import '../styles/App.css';

/**
 * Status options for client pipeline
 */
const statusOptions = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'active', label: 'Active' },
  { value: 'lost', label: 'Lost' },
  { value: 'archived', label: 'Archived' }
];

const thoughtPriorityOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'important', label: 'Important' },
  { value: 'casual', label: 'Casual' }
];

/**
 * Default empty form states
 */
const emptyClientForm = {
  name: '',
  company: '',
  email: '',
  phone: '',
  status: 'prospect',
  source: ''
};

const emptyTaskForm = { title: '', due_date: '' };
const emptyDocForm = { url: '', title: '' };
const createEmptyThoughtForm = () => ({
  text: '',
  date_thought: new Date().toISOString().split('T')[0],
  priority: 'casual'
});
const DASHBOARD_PATH = '/dashboard';
const DASHBOARD_ALL_PATH = '/dashboard/all';

/**
 * Hand Icon Component
 */
const HandIcon = ({ size = 40 }) => (
  <div
    role="img"
    aria-label="Handy Co Logo"
    style={{
      width: size,
      height: size,
      backgroundColor: 'var(--crm-text, #9CACA6)',
      WebkitMaskImage: 'url(/assets/icons/icon_handyco.svg)',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskImage: 'url(/assets/icons/icon_handyco.svg)',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      flexShrink: 0,
    }}
  />
);

/**
 * HandyCo hand icon as a client avatar option
 */
const HandyCoIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 3000 3000" xmlns="http://www.w3.org/2000/svg" style={{ fillRule: 'evenodd', clipRule: 'evenodd' }}>
    <g transform="matrix(4.4244,0,0,4.4244,-763.512,-749.867)">
      <path
        d="M469.35,296.88C471.517,281.367 479.09,270.373 492.07,263.9C501.98,258.97 513.43,258.2 523.51,262.48C538.363,268.787 546.78,279.927 548.76,295.9C548.814,296.312 549.168,296.623 549.583,296.623C549.692,296.623 549.799,296.602 549.9,296.56C556.947,293.6 564,292.783 571.06,294.11C585.51,296.83 599.02,308.88 602.86,323.19C603.66,326.17 604.057,330.753 604.05,336.94C604.05,374.927 604.04,412.843 604.02,450.69C604.02,450.693 604.02,450.696 604.02,450.699C604.02,450.913 604.196,451.089 604.41,451.089C604.515,451.089 604.617,451.046 604.69,450.97C618.84,437.02 639.41,426.89 659.24,427.98C664.66,428.28 664.37,432.59 664.38,437.26C664.5,492.507 664.463,547.753 664.27,603C664.237,611.653 663.75,619.143 662.81,625.47C658.67,653.417 647.713,678.313 629.94,700.16C610.46,724.12 585.973,740.797 556.48,750.19C505.82,766.34 452.23,754.63 412.11,720.14C383.98,695.96 364.99,661.82 359.82,624.7C359.14,619.813 358.8,613.167 358.8,604.76C358.807,538.667 358.797,472.577 358.77,406.49C358.76,390.59 358.14,382.04 364.01,370.82C373.46,352.77 392.95,346.17 412.5,352.32C412.827,352.42 412.997,352.297 413.01,351.95C413.123,346.123 413.227,340.303 413.32,334.49C413.553,319.93 419.647,308.657 431.6,300.67C443.393,292.777 455.83,291.597 468.91,297.13C469.17,297.237 469.317,297.153 469.35,296.88ZM517.16,491.72C520.22,485.287 521.75,479.547 521.75,474.5C521.783,418.06 521.773,361.623 521.72,305.19C521.72,299.22 520.74,294.51 515.91,291.36C511.16,288.25 504.9,288.7 500.67,292.54C498.043,294.92 496.73,297.907 496.73,301.5C496.737,372.253 496.783,442.977 496.87,513.67C496.87,513.671 496.87,513.672 496.87,513.673C496.87,513.882 497.042,514.053 497.25,514.053C497.325,514.053 497.398,514.031 497.46,513.99C506.173,508.437 512.74,501.013 517.16,491.72ZM467.39,336.13C467.34,318.61 442.6,317.07 442.06,335.11C442.007,336.903 441.98,338.7 441.98,340.5C441.827,407.407 441.93,474.247 442.29,541.02C442.29,541.021 442.29,541.022 442.29,541.023C442.29,541.242 442.471,541.423 442.69,541.423C442.753,541.423 442.814,541.408 442.87,541.38L466.85,529.68C467.163,529.527 467.32,529.273 467.32,528.92C467.6,474.647 467.623,410.383 467.39,336.13ZM550.47,480.8C547.46,509.19 529.31,532.05 504.01,544.48C465.437,563.44 426.84,582.383 388.22,601.31C387.913,601.463 387.753,601.713 387.74,602.06C385.87,644.65 409.72,684.7 444.33,708.19C455.69,715.903 468.897,721.473 483.95,724.9C484.197,724.953 484.28,724.86 484.2,724.62C462.16,657.07 506.62,584.84 575.61,571.32C575.97,571.253 576.15,571.037 576.15,570.67C575.977,492.277 575.867,413.887 575.82,335.5C575.8,318.5 551.04,317.5 551,336.5C550.92,379.393 550.91,422.29 550.97,465.19C550.983,472.297 550.817,477.5 550.47,480.8ZM412.72,555.84C412.824,555.79 412.89,555.685 412.89,555.57C412.983,501.41 413.007,447.22 412.96,393C412.953,386.9 410.483,382.7 405.55,380.4C398.13,376.95 387.3,381.33 387.3,390.5C387.273,449.68 387.303,508.76 387.39,567.74C387.394,568.05 387.65,568.303 387.96,568.303C388.05,568.303 388.14,568.281 388.22,568.24L412.72,555.84ZM605.42,598.11C582.667,596.85 562.21,603.087 544.05,616.82C524.537,631.58 512.927,651.053 509.22,675.24C506.7,691.687 508.74,707.213 515.34,721.82C516.053,723.393 517.173,725.14 518.7,727.06C518.888,727.298 519.179,727.433 519.48,727.42C574.07,725.03 618.07,684.3 632.03,632.72C635.1,621.37 635.22,610.51 635.23,598.25C635.263,554.337 635.273,510.413 635.26,466.48C635.256,466.225 635.045,466.018 634.79,466.018C634.702,466.018 634.615,466.043 634.54,466.09C611.48,481.03 606.19,505.64 606.14,531.87C606.107,553.777 606.05,575.687 605.97,597.6C605.97,597.96 605.787,598.13 605.42,598.11Z"
        style={{ fill: 'currentColor', fillRule: 'nonzero' }}
      />
    </g>
  </svg>
);

/**
 * Available icons for client avatars
 */
const CLIENT_ICONS = {
  // Brand
  HandyCo: HandyCoIcon,
  // People & work
  User, Users, Briefcase, Handshake, HandCoins,
  // Business & finance
  Buildings, Storefront, Warehouse, Factory, CurrencyDollar, Receipt, Tag, ChartBar, ChartLine, ChartPie, Scales,
  // Services & trades
  Wrench, Hammer, Scissors, Truck, Package,
  // Tech
  Code, Globe, Cpu, Desktop, DeviceMobile, Monitor, Printer, WifiHigh, HardDrive, Terminal, Database, Cloud, Robot, Gear, Bug,
  // Communication & media
  Megaphone, Headset, Microphone, Broadcast, Newspaper, VideoCamera, Camera, FilmStrip,
  // Creative
  PaintBrush, Palette, Pen, PenNib, Pencil, Guitar, MusicNote, Headphones,
  // Health & wellness
  Heartbeat, Heart, FirstAidKit, Stethoscope, Pill,
  // Food & drink
  ForkKnife, CookingPot, Coffee, Wine, BeerStein,
  // Sports & fitness
  Barbell, Bicycle, Football, Basketball, Baseball, GameController,
  // Nature & outdoors
  Leaf, Tree, Waves, Fish, Dog, Cat, PawPrint, SunHorizon, Umbrella, Tent, Campfire,
  // Education & knowledge
  GraduationCap, Book, Stack,
  // Travel & lifestyle
  Airplane, House, Flag,
  // Misc
  Star, Lightning, Fire, Smiley, Diamond, Crown, Rocket, ShieldCheck, TreeStructure, Bag,
};

const CLIENT_ICON_NAMES = Object.keys(CLIENT_ICONS);

/**
 * Renders a client avatar — icon if one is assigned, otherwise first letter
 */
const ClientAvatar = ({ client, size = 36 }) => {
  const IconComponent = client?.icon ? CLIENT_ICONS[client.icon] : null;
  const letter = (client?.company || client?.name || '?')[0].toUpperCase();
  return (
    <div className="crm-client-avatar" data-status={client?.status || 'prospect'} style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {IconComponent ? <IconComponent weight="fill" size={size * 0.5} /> : letter}
    </div>
  );
};

/**
 * Clickable avatar that opens an icon picker dropdown
 */
const ClientAvatarPicker = ({ client, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="crm-icon-picker-wrap" ref={ref}>
      <button type="button" className="crm-icon-picker-trigger" onClick={() => setOpen(!open)} title="Change icon">
        <ClientAvatar client={client} size={40} />
      </button>
      {open && (
        <div className="crm-icon-picker-dropdown">
          <button
            type="button"
            className={`crm-icon-picker-item ${!client?.icon ? 'active' : ''}`}
            onClick={() => { onSelect(null); setOpen(false); }}
            title="Default letter"
          >
            <span style={{ fontSize: 14, fontWeight: 600 }}>{(client?.company || client?.name || '?')[0].toUpperCase()}</span>
          </button>
          {CLIENT_ICON_NAMES.map(name => {
            const Icon = CLIENT_ICONS[name];
            return (
              <button
                key={name}
                type="button"
                className={`crm-icon-picker-item ${client?.icon === name ? 'active' : ''}`}
                onClick={() => { onSelect(name); setOpen(false); }}
                title={name}
              >
                <Icon weight="fill" size={18} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * CRM - Minimal Client Relationship Manager
 */
const Crm = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the "from" location for back button
  const backTo = location.state?.from || DASHBOARD_PATH;

  // Authentication state
  const [sessionToken, setSessionToken] = useState('');
  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // User dropdown menu
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Data state
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [detail, setDetail] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal & edit state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Form state
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [docForm, setDocForm] = useState(emptyDocForm);

  // Docs state
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsPage, setDocsPage] = useState(1);
  const [docsPagination, setDocsPagination] = useState({ total: 0, totalPages: 1, hasMore: false });
  const [showAddDocModal, setShowAddDocModal] = useState(false);

  // Thoughts state
  const [thoughtForm, setThoughtForm] = useState(createEmptyThoughtForm());
  const [thoughtLoading, setThoughtLoading] = useState(false);
  const [recentThoughts, setRecentThoughts] = useState([]);
  const [recentThoughtsLoading, setRecentThoughtsLoading] = useState(false);
  const [showThoughtModal, setShowThoughtModal] = useState(false);
  const [editingThought, setEditingThought] = useState(null); // null = new thought, object = editing

  // Milestones (What's Next) state
  const [milestones, setMilestones] = useState([]);
  const [milestoneLoading, setMilestoneLoading] = useState(false);
  const [milestoneInput, setMilestoneInput] = useState('');
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [milestoneHistoryExpanded, setMilestoneHistoryExpanded] = useState(false);

  // Email history state
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailThreads, setEmailThreads] = useState([]);
  const [emailConnection, setEmailConnection] = useState({
    connected: false,
    gmailEmail: null,
    clientEmails: [],
    expired: false
  });
  const [emailNextPageToken, setEmailNextPageToken] = useState(null);
  
  // Email detail views
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);

  // Weekly to-do view state
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, 1 = next week, -1 = last week
  const [allTasks, setAllTasks] = useState([]);
  const [allTasksLoading, setAllTasksLoading] = useState(false);

  // Theme state (persisted in localStorage)
  const [theme, setTheme] = useState(() => localStorage.getItem('crm_theme') || 'dark');

  // Task edit/add modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null); // null = new task, object = editing
  const [taskModalForm, setTaskModalForm] = useState({ title: '', due_date: '', client_id: '' });

  // Auth headers for API requests
  const authHeaders = useMemo(
    () => (sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {}),
    [sessionToken]
  );

  /**
   * Fire-and-forget activity logger — never blocks the UI
   */
  const logActivity = useCallback((event_type, label = null, metadata = null) => {
    if (!sessionToken) return;
    fetch('/api/crm/activity', {
      method: 'POST',
      headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type, label, metadata }),
    }).catch(() => {});
  }, [sessionToken]);

  // Helper: Get Monday of a given week (offset from current week)
  const getWeekBounds = useCallback((offset = 0) => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Sunday = go back 6, else go to Monday
    
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday + (offset * 7));
    monday.setHours(0, 0, 0, 0);
    
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return { monday, friday, sunday };
  }, []);

  // Format Date -> "YYYY-MM-DD" using local time (avoid UTC shift)
  const formatDateKey = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Parse "YYYY-MM-DD" into a local Date (no timezone drift)
  const parseDateKey = useCallback((dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 12); // noon keeps us inside the intended day
  }, []);

  // Recent clients for dashboard (sorted by last activity, excluding archived, max 10)
  const recentClients = useMemo(() => {
    return clients
      .filter(c => c.status !== 'archived')
      .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
      .slice(0, 10);
  }, [clients]);

  // Total non-archived client count
  const totalActiveClients = useMemo(() => {
    return clients.filter(c => c.status !== 'archived').length;
  }, [clients]);


  // Week-grouped tasks for the to-do panel
  const weeklyTasks = useMemo(() => {
    const { monday, sunday } = getWeekBounds(weekOffset);
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    
    // Initialize days
    const grouped = {};
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      const dateStr = formatDateKey(date);
      grouped[dateStr] = {
        label: days[i],
        date: dateStr,
        displayDate: `${date.getMonth() + 1}/${date.getDate()}`,
        tasks: []
      };
    }
    
    // Filter and group tasks
    const weekTasks = allTasks.filter(task => {
      if (!task.due_date) return false;
      const taskDate = parseDateKey(task.due_date);
      if (!taskDate) return false;
      return taskDate >= monday && taskDate <= sunday;
    });
    
    weekTasks.forEach(task => {
      const dateStr = task.due_date;
      if (grouped[dateStr]) {
        grouped[dateStr].tasks.push(task);
      }
    });
    
    // Sort tasks within each day
    Object.values(grouped).forEach(day => {
      day.tasks.sort((a, b) => (a.completed || 0) - (b.completed || 0));
    });
    
    const weekStart = monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const totalTasks = weekTasks.filter(t => !t.completed).length;
    
    const completedThisWeek = weekTasks.filter(t => t.completed).length;
    const allCount = weekTasks.length;
    const progress = allCount > 0 ? Math.round((completedThisWeek / allCount) * 100) : 0;

    return {
      days: Object.values(grouped),
      weekStart,
      totalTasks,
      completedThisWeek,
      allCount,
      progress,
      monday
    };
  }, [allTasks, weekOffset, getWeekBounds, formatDateKey, parseDateKey]);

  // Get latest email for preview in client card
  const latestEmailThread = useMemo(() => {
    if (emailThreads.length === 0) return null;
    return emailThreads[0]; // Already sorted newest first
  }, [emailThreads]);

  /**
   * Check for existing session on mount
   */
  useEffect(() => {
    const checkSession = async () => {
      const storedToken = localStorage.getItem('crm_session');
      const storedUser = localStorage.getItem('crm_user');

      if (!storedToken) {
        setAuthLoading(false);
        return;
      }

      try {
        // Validate the session with the server
        const res = await fetch('/api/crm/auth/session', {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        const data = await res.json();

        if (res.ok && data.valid) {
          setSessionToken(storedToken);
          setUser(storedUser ? JSON.parse(storedUser) : data.user);
          setAuthenticated(true);

          if (localStorage.getItem('crm_sign_in_pending')) {
            localStorage.removeItem('crm_sign_in_pending');
            fetch('/api/crm/activity', {
              method: 'POST',
              headers: { Authorization: `Bearer ${storedToken}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ event_type: 'sign_in' }),
            }).catch(() => {});
          }
        } else {
          // Clear invalid session
          localStorage.removeItem('crm_session');
          localStorage.removeItem('crm_user');
        }
      } catch (err) {
        console.error('Session check error:', err);
        localStorage.removeItem('crm_session');
        localStorage.removeItem('crm_user');
      } finally {
        setAuthLoading(false);
      }
    };

    checkSession();
  }, []);

  /**
   * Log page view once per authenticated session load
   */
  useEffect(() => {
    if (!authenticated || !sessionToken) return;
    const path = window.location.pathname;
    const label = clientId ? `Client #${clientId}` : 'Dashboard';
    logActivity('page_view', label, { path });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, sessionToken]);

  /**
   * Handle Google OAuth sign in
   */
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/crm/auth/google');
      const data = await res.json();

      if (!res.ok || !data.authUrl) {
        throw new Error(data.error || 'Unable to start sign in');
      }

      // Redirect to Google OAuth
      window.location.href = data.authUrl;
    } catch (err) {
      setError(err.message || 'Unable to sign in');
      setLoading(false);
    }
  };

  /**
   * Close user dropdown when clicking outside it
   */
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleOutsideClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [userMenuOpen]);

  /**
   * Handle sign out
   */
  const handleSignOut = () => {
    localStorage.removeItem('crm_session');
    localStorage.removeItem('crm_user');
    setSessionToken('');
    setUser(null);
    setAuthenticated(false);
    setClients([]);
    setDetail(null);
    setRecentThoughts([]);
    setThoughtForm(createEmptyThoughtForm());
    setThoughtLoading(false);
    navigate(DASHBOARD_PATH);
  };

  /**
   * Toggle light/dark theme
   */
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('crm_theme', next);
  };

  /**
   * Load all clients from the API
   */
  const loadClients = useCallback(
    async () => {
      if (!sessionToken) return;
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/api/crm/clients', {
          headers: authHeaders
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to load clients');
        setClients(data.clients || []);
      } catch (err) {
        setError(err.message || 'Unable to load clients');
        // Don't log out on load error - could be network issue
      } finally {
        setLoading(false);
      }
    },
    [sessionToken, authHeaders]
  );

  /**
   * Load a handful of recent thoughts for the dashboard
   */
  const loadRecentThoughts = useCallback(
    async () => {
      if (!sessionToken) return;
      setRecentThoughtsLoading(true);
      try {
        const res = await fetch('/api/crm/thoughts?limit=6', {
          headers: authHeaders
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to load thoughts');
        setRecentThoughts(data.thoughts || []);
      } catch (err) {
        console.error('Load recent thoughts error:', err);
      } finally {
        setRecentThoughtsLoading(false);
      }
    },
    [sessionToken, authHeaders]
  );

  /**
   * Load a single client's details and tasks
   */
  const loadClientDetail = useCallback(
    async (id) => {
      if (!id || !sessionToken) return;
      setTaskLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/crm/clients/${id}`, {
          headers: authHeaders
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to load client');

        setDetail({ client: data.client, tasks: data.tasks || [], thoughts: data.thoughts || [] });
        setThoughtForm(createEmptyThoughtForm());
        setClients((prev) => {
          if (!data.client) return prev;
          const exists = prev.find((c) => c.id === data.client.id);
          if (exists) {
            return prev.map((c) => (c.id === data.client.id ? data.client : c));
          }
          return [data.client, ...prev];
        });

        // Load milestones for this client
        try {
          const mRes = await fetch(`/api/crm/clients/${id}/milestones`, { headers: authHeaders });
          if (mRes.ok) {
            const mData = await mRes.json();
            setMilestones(mData.milestones || []);
          }
        } catch (_) { /* non-critical */ }
        setMilestoneInput('');
        setShowMilestoneForm(false);
        setMilestoneHistoryExpanded(false);
      } catch (err) {
        setError(err.message || 'Unable to load client');
        setDetail(null);
      } finally {
        setTaskLoading(false);
      }
    },
    [sessionToken, authHeaders]
  );

  /**
   * Load docs for a client with pagination
   */
  const loadDocs = useCallback(
    async (id, page = 1) => {
      if (!id || !sessionToken) return;
      setDocsLoading(true);
      try {
        const res = await fetch(`/api/crm/clients/${id}/docs?page=${page}`, {
          headers: authHeaders
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to load docs');

        setDocs(data.docs || []);
        setDocsPagination(data.pagination || { total: 0, totalPages: 1, hasMore: false });
        setDocsPage(page);
      } catch (err) {
        console.error('Load docs error:', err);
      } finally {
        setDocsLoading(false);
      }
    },
    [sessionToken, authHeaders]
  );

  /**
   * Add a new doc to the current client
   */
  const handleAddDoc = async (e) => {
    e.preventDefault();
    if (!detail?.client?.id || !docForm.url.trim()) {
      return;
    }

    setDocsLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/crm/clients/${detail.client.id}/docs`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(docForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to add document');

      // Reload docs to get fresh list
      await loadDocs(detail.client.id, 1);
      setDocForm(emptyDocForm);
      setShowAddDocModal(false);
    } catch (err) {
      setError(err.message || 'Unable to add document');
    } finally {
      setDocsLoading(false);
    }
  };

  /**
   * Delete a doc
   */
  const handleDeleteDoc = async (docId) => {
    if (!detail?.client?.id) return;
    
    setDocsLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/crm/clients/${detail.client.id}/docs`, {
        method: 'DELETE',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to delete document');

      // Remove doc from local state
      setDocs((prev) => prev.filter((d) => d.id !== docId));
      setDocsPagination((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        totalPages: Math.ceil(Math.max(0, prev.total - 1) / 10)
      }));
    } catch (err) {
      setError(err.message || 'Unable to delete document');
    } finally {
      setDocsLoading(false);
    }
  };

  /**
   * Get doc type icon and color
   */
  const getDocTypeInfo = (docType) => {
    const types = {
      document: { icon: <FileText weight="fill" size={16} />, color: '#4285F4', label: 'Doc' },
      spreadsheet: { icon: <Table weight="fill" size={16} />, color: '#0F9D58', label: 'Sheet' },
      slides: { icon: <Presentation weight="fill" size={16} />, color: '#F4B400', label: 'Slides' },
      form: { icon: <ClipboardText weight="fill" size={16} />, color: '#7627BB', label: 'Form' },
      drawing: { icon: <Palette weight="fill" size={16} />, color: '#DB4437', label: 'Drawing' }
    };
    return types[docType] || types.document;
  };

  /**
   * Open new Google Doc in GSuite
   */
  const openNewGoogleDoc = (type = 'document') => {
    const urls = {
      document: 'https://docs.google.com/document/create',
      spreadsheet: 'https://docs.google.com/spreadsheets/create',
      slides: 'https://docs.google.com/presentation/create',
      form: 'https://docs.google.com/forms/create'
    };
    window.open(urls[type] || urls.document, '_blank');
  };

  /**
   * Load email history for the current client (with threading)
   */
  const loadEmailHistory = useCallback(
    async (id, pageToken = null) => {
      if (!id || !sessionToken) return;
      setEmailsLoading(true);
      try {
        let url = `/api/crm/clients/${id}/emails?limit=20`;
        if (pageToken) {
          url += `&pageToken=${encodeURIComponent(pageToken)}`;
        }
        
        const res = await fetch(url, {
          headers: authHeaders
        });
        const data = await res.json();
        
        if (!res.ok) {
          throw new Error(data.error || 'Failed to load emails');
        }

        setEmailConnection({
          connected: data.connected || false,
          gmailEmail: data.gmailEmail || null,
          clientEmails: data.clientEmails || [],
          expired: data.expired || false
        });

        if (data.connected && data.threads) {
          if (pageToken) {
            // Append to existing threads for pagination
            setEmailThreads(prev => [...prev, ...data.threads]);
          } else {
            setEmailThreads(data.threads);
          }
          setEmailNextPageToken(data.nextPageToken || null);
        } else {
          setEmailThreads([]);
          setEmailNextPageToken(null);
        }
      } catch (err) {
        console.error('Load emails error:', err);
        setError(err.message || 'Failed to load email history');
      } finally {
        setEmailsLoading(false);
      }
    },
    [sessionToken, authHeaders]
  );

  /**
   * Load all tasks across all clients (for weekly view)
   */
  const loadAllTasks = useCallback(
    async (clientsList) => {
      if (!sessionToken || !clientsList?.length) return;
      setAllTasksLoading(true);
      try {
        // Fetch all clients' tasks
        const tasksPromises = clientsList.map(async (client) => {
          const res = await fetch(`/api/crm/clients/${client.id}/tasks`, {
            headers: authHeaders
          });
          if (!res.ok) return [];
          const data = await res.json();
          return (data.tasks || []).map(task => ({
            ...task,
            clientName: client.company || client.name || 'Unknown',
            clientId: client.id
          }));
        });
        
        const allTasksArrays = await Promise.all(tasksPromises);
        const flatTasks = allTasksArrays.flat();
        setAllTasks(flatTasks);
      } catch (err) {
        console.error('Load all tasks error:', err);
      } finally {
        setAllTasksLoading(false);
      }
    },
    [sessionToken, authHeaders]
  );

  /**
   * Toggle task completion from weekly view
   */
  const toggleTaskFromWeekly = async (task) => {
    try {
      const res = await fetch(`/api/crm/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: task.completed ? 0 : 1 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to update task');

      // Update local state
      setAllTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: data.task.completed } : t));
      
      // Update clients task count
      setClients(prev =>
        prev.map(c =>
          c.id === task.clientId
            ? {
                ...c,
                open_tasks: data.task.completed
                  ? Math.max(0, (c.open_tasks || 1) - 1)
                  : (c.open_tasks || 0) + 1
              }
            : c
        )
      );
    } catch (err) {
      setError(err.message || 'Unable to update task');
    }
  };

  /**
   * Open task modal for adding a new task
   */
  const openAddTaskModal = (defaultDate = '') => {
    setEditingTask(null);
    setTaskModalForm({ title: '', due_date: defaultDate, client_id: '' });
    setShowTaskModal(true);
  };

  /**
   * Open task modal for editing an existing task
   */
  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setTaskModalForm({
      title: task.title || '',
      due_date: task.due_date || '',
      client_id: task.client_id || task.clientId || ''
    });
    setShowTaskModal(true);
  };

  /**
   * Save task (create or update) from modal
   */
  const handleSaveTaskModal = async (e) => {
    e.preventDefault();
    if (!taskModalForm.title.trim()) return;

    setTaskLoading(true);
    setError('');

    try {
      if (editingTask) {
        // Update existing task
        const res = await fetch(`/api/crm/tasks/${editingTask.id}`, {
          method: 'PATCH',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: taskModalForm.title,
            due_date: taskModalForm.due_date || null
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to update task');

        // Update allTasks state
        setAllTasks(prev => prev.map(t => 
          t.id === editingTask.id 
            ? { ...t, title: data.task.title, due_date: data.task.due_date }
            : t
        ));

        // Update detail tasks if on client page
        if (detail?.tasks) {
          setDetail(prev => ({
            ...prev,
            tasks: prev.tasks.map(t => t.id === editingTask.id ? data.task : t)
          }));
        }
      } else {
        // Create new task
        const clientId = taskModalForm.client_id;
        if (!clientId) {
          setError('Please select a client');
          setTaskLoading(false);
          return;
        }

        const res = await fetch(`/api/crm/clients/${clientId}/tasks`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: taskModalForm.title,
            due_date: taskModalForm.due_date || null
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to create task');

        // Find client info for the task
        const client = clients.find(c => c.id === parseInt(clientId));
        const newTask = {
          ...data.task,
          clientName: client?.company || client?.name || 'Unknown',
          clientId: parseInt(clientId)
        };

        // Add to allTasks
        setAllTasks(prev => [...prev, newTask]);

        // Update client task count
        setClients(prev =>
          prev.map(c =>
            c.id === parseInt(clientId)
              ? { ...c, open_tasks: (c.open_tasks || 0) + 1 }
              : c
          )
        );

        // Update detail tasks if on that client's page
        if (detail?.client?.id === parseInt(clientId)) {
          setDetail(prev => ({
            ...prev,
            tasks: [data.task, ...(prev?.tasks || [])]
          }));
        }

        logActivity('task_created', taskModalForm.title, { client_id: parseInt(clientId) });
      }

      setShowTaskModal(false);
      setTaskModalForm({ title: '', due_date: '', client_id: '' });
      setEditingTask(null);
    } catch (err) {
      setError(err.message || 'Unable to save task');
    } finally {
      setTaskLoading(false);
    }
  };

  /**
   * Delete a task
   */
  const handleDeleteTask = async (task) => {
    if (!window.confirm('Delete this task?')) return;

    try {
      const taskClientId = task.client_id || task.clientId;
      const res = await fetch(`/api/crm/tasks/${task.id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Unable to delete task');
      }

      // Remove from allTasks
      setAllTasks(prev => prev.filter(t => t.id !== task.id));

      // Update client task count if task was open
      if (!task.completed) {
        setClients(prev =>
          prev.map(c =>
            c.id === taskClientId
              ? { ...c, open_tasks: Math.max(0, (c.open_tasks || 1) - 1) }
              : c
          )
        );
      }

      // Update detail tasks if on client page
      if (detail?.tasks) {
        setDetail(prev => ({
          ...prev,
          tasks: prev.tasks.filter(t => t.id !== task.id)
        }));
      }

      logActivity('task_deleted', task.title, { task_id: task.id });
      setShowTaskModal(false);
    } catch (err) {
      setError(err.message || 'Unable to delete task');
    }
  };

  /**
   * Load full thread details
   */
  const loadThread = async (threadId) => {
    if (!detail?.client?.id) return;
    setThreadLoading(true);
    try {
      const res = await fetch(
        `/api/crm/clients/${detail.client.id}/emails?threadId=${threadId}`,
        { headers: authHeaders }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load thread');
      setSelectedThread(data.thread);
    } catch (err) {
      setError(err.message || 'Failed to load thread');
    } finally {
      setThreadLoading(false);
    }
  };

  /**
   * Load full message content
   */
  const loadMessage = async (messageId) => {
    if (!detail?.client?.id) return;
    setMessageLoading(true);
    try {
      const res = await fetch(
        `/api/crm/clients/${detail.client.id}/emails?messageId=${messageId}`,
        { headers: authHeaders }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load message');
      setSelectedMessage(data.message);
    } catch (err) {
      setError(err.message || 'Failed to load message');
    } finally {
      setMessageLoading(false);
    }
  };

  /**
   * Start Gmail OAuth connection flow
   */
  const connectGmail = async () => {
    if (!detail?.client?.id) return;
    
    try {
      const res = await fetch(`/api/crm/gmail/connect?clientId=${detail.client.id}`, {
        headers: authHeaders
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to start Gmail connection');
      }

      // Open OAuth popup
      const popup = window.open(
        data.authUrl,
        'gmail-oauth',
        'width=500,height=600,menubar=no,toolbar=no,location=no,status=no'
      );

      // Listen for OAuth callback message
      const handleMessage = (event) => {
        if (event.data?.type === 'gmail-oauth-callback') {
          window.removeEventListener('message', handleMessage);
          if (event.data.success) {
            // Reload email history after successful connection
            loadEmailHistory(detail.client.id);
          } else {
            setError(event.data.message || 'Gmail connection failed');
          }
        }
      };
      window.addEventListener('message', handleMessage);

      // Cleanup if popup is closed manually
      const checkClosed = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
        }
      }, 500);

    } catch (err) {
      setError(err.message || 'Failed to connect Gmail');
    }
  };

  /**
   * Disconnect Gmail from the current client
   */
  const disconnectGmail = async () => {
    if (!detail?.client?.id) return;
    if (!window.confirm('Disconnect Gmail? You can reconnect later.')) return;

    try {
      const res = await fetch(`/api/crm/clients/${detail.client.id}/emails`, {
        method: 'DELETE',
        headers: authHeaders
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to disconnect Gmail');
      }

      setEmailConnection({
        connected: false,
        gmailEmail: null,
        clientEmails: emailConnection.clientEmails,
        expired: false
      });
      setEmailThreads([]);
      setEmailNextPageToken(null);
    } catch (err) {
      setError(err.message || 'Failed to disconnect Gmail');
    }
  };

  /**
   * Format email date for display
   */
  const formatEmailDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    
    // Compare dates by resetting time to midnight for accurate day comparison
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffTime = nowDay.getTime() - dateDay.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  /**
   * Format thought date for display
   * Handles YYYY-MM-DD strings as local dates (not UTC) to avoid off-by-one errors
   */
  const formatThoughtDate = (value) => {
    if (!value) return '';
    let parsed;
    // If it's a YYYY-MM-DD string, parse as local date to avoid timezone shift
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      parsed = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      parsed = new Date(value);
    }
    if (Number.isNaN(parsed.getTime())) return value;
    const now = new Date();
    const sameYear = parsed.getFullYear() === now.getFullYear();
    return parsed.toLocaleDateString([], { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
  };

  /**
   * Map priority values to labels
   */
  const getPriorityLabel = (value) => {
    const match = thoughtPriorityOptions.find((opt) => opt.value === (value || '').toLowerCase());
    return match ? match.label : 'Casual';
  };

  /**
   * Determine thought card size based on text length
   * - small: 1x1 (under 50 chars)
   * - wide: 2x1 (50-100 chars, short horizontal)
   * - tall: 1x2 (100-180 chars, more vertical)
   * - large: 2x2 (180+ chars)
   */
  const getThoughtSize = (text) => {
    if (!text) return 'small';
    const len = text.length;
    if (len < 50) return 'small';
    if (len < 100) return 'wide';
    if (len < 180) return 'tall';
    return 'large';
  };

  /**
   * Format full date for message view
   */
  const formatFullDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Handle adding additional email
   */
  const handleAddAdditionalEmail = () => {
    const currentEmails = editForm.additional_emails || [];
    setEditForm({
      ...editForm,
      additional_emails: [...currentEmails, '']
    });
  };

  /**
   * Handle removing additional email
   */
  const handleRemoveAdditionalEmail = (index) => {
    const currentEmails = [...(editForm.additional_emails || [])];
    currentEmails.splice(index, 1);
    setEditForm({
      ...editForm,
      additional_emails: currentEmails
    });
  };

  /**
   * Handle updating additional email
   */
  const handleUpdateAdditionalEmail = (index, value) => {
    const currentEmails = [...(editForm.additional_emails || [])];
    currentEmails[index] = value;
    setEditForm({
      ...editForm,
      additional_emails: currentEmails
    });
  };

  /**
   * Handle adding a new (empty) Slack channel entry
   */
  const handleAddSlackChannel = () => {
    const current = editForm.slack_channels || [];
    setEditForm({ ...editForm, slack_channels: [...current, { name: '', url: '' }] });
  };

  /**
   * Handle removing a Slack channel by index
   */
  const handleRemoveSlackChannel = (index) => {
    const updated = [...(editForm.slack_channels || [])];
    updated.splice(index, 1);
    setEditForm({ ...editForm, slack_channels: updated });
  };

  /**
   * Handle updating a field on a Slack channel entry
   */
  const handleUpdateSlackChannel = (index, field, value) => {
    const updated = [...(editForm.slack_channels || [])];
    updated[index] = { ...updated[index], [field]: value };
    setEditForm({ ...editForm, slack_channels: updated });
  };

  // Load clients on authentication
  useEffect(() => {
    if (authenticated) {
      loadClients();
    }
  }, [authenticated, loadClients]);

  // Load recent thoughts when on the dashboard
  useEffect(() => {
    if (authenticated && !clientId) {
      loadRecentThoughts();
    }
  }, [authenticated, clientId, loadRecentThoughts]);

  // Load all tasks when clients are loaded (for weekly view)
  useEffect(() => {
    if (authenticated && clients.length > 0 && !clientId) {
      loadAllTasks(clients);
    }
  }, [authenticated, clients, clientId, loadAllTasks]);

  // Load client detail when clientId changes
  useEffect(() => {
    if (authenticated && clientId) {
      loadClientDetail(clientId);
      loadDocs(clientId, 1);
      setIsEditing(false);
      // Reset email state
      setEmailThreads([]);
      setSelectedThread(null);
      setSelectedMessage(null);
    } else if (!clientId) {
      setDetail(null);
      setDocs([]);
      setDocsPage(1);
      setIsEditing(false);
      setEmailThreads([]);
    }
  }, [authenticated, clientId, loadClientDetail, loadDocs]);

  // Load email history when client loads (for preview)
  useEffect(() => {
    if (authenticated && clientId && detail?.client?.id) {
      loadEmailHistory(detail.client.id);
    }
  }, [authenticated, clientId, detail?.client?.id, loadEmailHistory]);

  // Initialize edit form when entering edit mode
  useEffect(() => {
    if (isEditing && detail?.client) {
      setEditForm({
        name: detail.client.name || '',
        company: detail.client.company || '',
        email: detail.client.email || '',
        phone: detail.client.phone || '',
        source: detail.client.source || '',
        status: detail.client.status || 'prospect',
        additional_emails: detail.client.additional_emails || [],
        lifetime_value: detail.client.lifetime_value || 0,
        slack_channels: detail.client.slack_channels || []
      });
    }
  }, [isEditing, detail?.client]);

  /**
   * Create a new client
   */
  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!clientForm.company.trim()) {
      setError('Company name is required');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/crm/clients', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(clientForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to add client');

      setClients((prev) => [data.client, ...prev]);
      setClientForm(emptyClientForm);
      setShowAddModal(false);
      navigate(`${DASHBOARD_PATH}/${data.client.id}`);
      setDetail({ client: data.client, tasks: [], thoughts: [] });
      logActivity('client_created', data.client.company || data.client.name, { client_id: data.client.id });
    } catch (err) {
      setError(err.message || 'Unable to add client');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save all edited client fields
   */
  const handleSaveClient = async () => {
    if (!detail?.client?.id) return;
    setLoading(true);
    setError('');
    try {
      // Filter out empty/incomplete entries before saving
      const cleanedForm = {
        ...editForm,
        additional_emails: (editForm.additional_emails || []).filter(e => e && e.trim()),
        slack_channels: (editForm.slack_channels || []).filter(ch => ch.name && ch.name.trim())
      };

      const res = await fetch(`/api/crm/clients/${detail.client.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanedForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to update client');

      setDetail((prev) => ({ ...prev, client: data.client }));
      setClients((prev) => prev.map((c) => (c.id === data.client.id ? data.client : c)));
      setIsEditing(false);
      logActivity('client_updated', data.client.company || data.client.name, { client_id: data.client.id });
      // Reload emails if emails changed
      loadEmailHistory(detail.client.id);
    } catch (err) {
      setError(err.message || 'Unable to update client');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancel editing and reset form
   */
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  /**
   * Open thought modal for adding a new thought
   */
  const openAddThoughtModal = () => {
    setEditingThought(null);
    setThoughtForm(createEmptyThoughtForm());
    setShowThoughtModal(true);
  };

  /**
   * Open thought modal for editing an existing thought
   */
  const openEditThoughtModal = (thought) => {
    setEditingThought(thought);
    setThoughtForm({
      text: thought.text || '',
      priority: thought.priority || 'casual',
      date_thought: thought.date_thought || new Date().toISOString().split('T')[0]
    });
    setShowThoughtModal(true);
  };

  /**
   * Add or update a thought (via modal)
   */
  const handleSaveThought = async (e) => {
    e.preventDefault();
    const clientIdForThought = detail?.client?.id;
    if (!clientIdForThought || !thoughtForm.text.trim()) return;

    setThoughtLoading(true);
    setError('');

    const payload = {
      text: thoughtForm.text.trim(),
      priority: thoughtForm.priority || 'casual',
      date_thought: thoughtForm.date_thought || new Date().toISOString().split('T')[0]
    };

    try {
      // If editing, update existing thought
      if (editingThought) {
        const res = await fetch(`/api/crm/thoughts/${editingThought.id}`, {
          method: 'PATCH',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to update thought');

        // Update local state
        setDetail((prev) => {
          if (!prev?.client) return prev;
          const updatedThoughts = (prev.thoughts || []).map((t) =>
            t.id === editingThought.id ? { ...t, ...payload } : t
          );
          const latest = updatedThoughts[0] || null;
          return {
            ...prev,
            thoughts: updatedThoughts,
            client: {
              ...prev.client,
              latest_thought_text: latest?.text || null,
              latest_thought_priority: latest?.priority || null,
              latest_thought_date: latest?.date_thought || null
            }
          };
        });

        setClients((prev) =>
          prev.map((c) => {
            if (c.id !== clientIdForThought) return c;
            return {
              ...c,
              latest_thought_text: payload.text,
              latest_thought_priority: payload.priority,
              latest_thought_date: payload.date_thought
            };
          })
        );
        logActivity('thought_updated', payload.text.substring(0, 60), { client_id: clientIdForThought });
      } else {
        // Create new thought
        const res = await fetch(`/api/crm/clients/${clientIdForThought}/thoughts`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to add thought');

        setDetail((prev) => {
          if (!prev?.client) return prev;
          const nextThoughts = [data.thought, ...(prev.thoughts || [])];
          return {
            ...prev,
            thoughts: nextThoughts,
            client: {
              ...prev.client,
              latest_thought_text: data.thought.text,
              latest_thought_priority: data.thought.priority,
              latest_thought_date: data.thought.date_thought,
              total_thoughts: (prev.client.total_thoughts || 0) + 1
            }
          };
        });

        setClients((prev) =>
          prev.map((c) =>
            c.id === clientIdForThought
              ? {
                  ...c,
                  latest_thought_text: data.thought.text,
                  latest_thought_priority: data.thought.priority,
                  latest_thought_date: data.thought.date_thought,
                  total_thoughts: (c.total_thoughts || 0) + 1
                }
              : c
          )
        );
        logActivity('thought_added', data.thought.text.substring(0, 60), { client_id: clientIdForThought });
      }

      setThoughtForm(createEmptyThoughtForm());
      setShowThoughtModal(false);
      setEditingThought(null);
      loadRecentThoughts();
    } catch (err) {
      setError(err.message || 'Unable to save thought');
    } finally {
      setThoughtLoading(false);
    }
  };

  /**
   * Delete a thought
   */
  const handleDeleteThought = async (thoughtId, skipConfirm = false) => {
    if (!thoughtId || !detail?.client?.id) return;
    if (!skipConfirm && !window.confirm('Delete this thought?')) return;

    setThoughtLoading(true);
    try {
      const res = await fetch(`/api/crm/thoughts/${thoughtId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Unable to delete thought');
      }

      const clientIdForThought = detail.client.id;
      let nextThoughts = [];
      let latest = null;

      setDetail((prev) => {
        if (!prev) return prev;
        nextThoughts = (prev.thoughts || []).filter((t) => t.id !== thoughtId);
        latest = nextThoughts[0] || null;
        return {
          ...prev,
          thoughts: nextThoughts,
          client: prev.client
            ? {
                ...prev.client,
                latest_thought_text: latest?.text || null,
                latest_thought_priority: latest?.priority || null,
                latest_thought_date: latest?.date_thought || null,
                total_thoughts: nextThoughts.length
              }
            : prev.client
        };
      });

      setClients((prev) =>
        prev.map((c) =>
          c.id === clientIdForThought
            ? {
                ...c,
                latest_thought_text: latest?.text || null,
                latest_thought_priority: latest?.priority || null,
                latest_thought_date: latest?.date_thought || null,
                total_thoughts: nextThoughts.length
              }
            : c
        )
      );

      setRecentThoughts((prev) => prev.filter((t) => t.id !== thoughtId));
      loadRecentThoughts();
    } catch (err) {
      setError(err.message || 'Unable to delete thought');
    } finally {
      setThoughtLoading(false);
    }
  };

  /**
   * Add a new task to the current client
   */
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!detail?.client?.id || !taskForm.title.trim()) {
      return;
    }

    setTaskLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/crm/clients/${detail.client.id}/tasks`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(taskForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to add task');

      setDetail((prev) => ({
        ...prev,
        tasks: [data.task, ...(prev?.tasks || [])]
      }));
      setClients((prev) =>
        prev.map((c) =>
          c.id === detail.client.id ? { ...c, open_tasks: (c.open_tasks || 0) + 1 } : c
        )
      );
      setTaskForm(emptyTaskForm);
    } catch (err) {
      setError(err.message || 'Unable to add task');
    } finally {
      setTaskLoading(false);
    }
  };

  /**
   * Toggle a task's completed status
   */
  const toggleTask = async (task) => {
    setTaskLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/crm/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: task.completed ? 0 : 1 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to update task');

      setDetail((prev) => ({
        ...prev,
        tasks: (prev?.tasks || []).map((t) => (t.id === task.id ? data.task : t))
      }));

      setClients((prev) =>
        prev.map((c) =>
          c.id === task.client_id
            ? {
                ...c,
                open_tasks: data.task.completed
                  ? Math.max(0, (c.open_tasks || 1) - 1)
                  : (c.open_tasks || 0) + 1
              }
            : c
        )
      );

      if (data.task.completed) {
        logActivity('task_completed', task.title, { task_id: task.id });
      }
    } catch (err) {
      setError(err.message || 'Unable to update task');
    } finally {
      setTaskLoading(false);
    }
  };

  /**
   * Close email popup and reset views
   */
  const closeEmailHistory = () => {
    setShowEmailHistory(false);
    setSelectedThread(null);
    setSelectedMessage(null);
  };

  /**
   * Go back from message view to thread view
   */
  const backToThread = () => {
    setSelectedMessage(null);
  };

  /**
   * Go back from thread view to list view
   */
  const backToList = () => {
    setSelectedThread(null);
    setSelectedMessage(null);
  };

  // ── Milestone (What's Next) handlers ──
  const handleAddMilestone = async (e) => {
    e.preventDefault();
    if (!milestoneInput.trim() || !detail?.client?.id) return;
    setMilestoneLoading(true);
    try {
      const res = await fetch(`/api/crm/clients/${detail.client.id}/milestones`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: milestoneInput.trim() })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setMilestones(prev => {
        const withoutActive = prev.map(m => m.status === 'active' ? { ...m, status: 'done', completed_at: new Date().toISOString() } : m);
        return [data.milestone, ...withoutActive];
      });
      setMilestoneInput('');
      setShowMilestoneForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setMilestoneLoading(false);
    }
  };

  const handleCompleteMilestone = async () => {
    if (!detail?.client?.id) return;
    setMilestoneLoading(true);
    try {
      const res = await fetch(`/api/crm/clients/${detail.client.id}/milestones`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'complete' })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setMilestones(data.milestones || []);
      setShowMilestoneForm(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setMilestoneLoading(false);
    }
  };

  const handleDismissMilestone = async () => {
    if (!detail?.client?.id) return;
    setMilestoneLoading(true);
    try {
      const res = await fetch(`/api/crm/clients/${detail.client.id}/milestones`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'dismiss' })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed'); }
      const data = await res.json();
      setMilestones(data.milestones || []);
      setShowMilestoneForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setMilestoneLoading(false);
    }
  };

  const activeMilestone = milestones.find(m => m.status === 'active');
  const milestoneHistory = milestones.filter(m => m.status !== 'active');

  const activeClient = detail?.client;
  const openTasks = (detail?.tasks || []).filter(t => !t.completed);
  const completedTasks = (detail?.tasks || []).filter(t => t.completed);
  const thoughts = detail?.thoughts || [];

  // Get all emails for display
  const allClientEmails = useMemo(() => {
    const emails = [];
    if (activeClient?.email) emails.push(activeClient.email);
    if (activeClient?.additional_emails?.length) {
      emails.push(...activeClient.additional_emails);
    }
    return emails;
  }, [activeClient]);

  // Collapsible completed tasks state
  const [completedTasksCollapsed, setCompletedTasksCollapsed] = useState(true);

  // ============================================
  // LOADING SCREEN (checking session)
  // ============================================
  if (authLoading) {
    return (
      <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
        <div className="crm-login-wrapper">
          <div className="crm-login">
            <HandIcon size={80} />
            <h1>Clients</h1>
            <div className="crm-auth-loading">
              <div className="crm-spinner" />
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // LOGIN SCREEN
  // ============================================
  if (!authenticated) {
    return (
      <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
        <div className="crm-login-wrapper">
          <div className="crm-login">
            <HandIcon size={80} />
            <h1>Clients</h1>
            <p>Sign in with your Google account to continue.</p>
            {error && <div className="crm-error">{error}</div>}
            <button 
              className="crm-button crm-google-btn" 
              onClick={handleGoogleSignIn} 
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Redirecting…' : 'Sign in with Google'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // AUTHENTICATED VIEW
  // ============================================
  return (
    <div className={`crm-page ${theme === 'light' ? 'crm-light' : ''}`}>
      {/* Header */}
      <header className="crm-header">
        <div className="crm-header-brand" onClick={() => navigate(DASHBOARD_PATH)}>
          <HandIcon size={36} />
          <span className="crm-header-title">
            <span className="crm-header-wordmark">HANDY CO</span>
          </span>
        </div>
        <nav className="crm-header-nav">
          <button className={`crm-nav-link ${!clientId ? 'active' : ''}`} onClick={() => navigate('/dashboard')}>Overview</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/all')}>Clients</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/tasks')}>Tasks</button>
          <button className="crm-nav-link" onClick={() => navigate('/dashboard/thoughts')}>Thoughts</button>
          <button className="crm-nav-link" onClick={() => navigate('/ds')}>Design Systems</button>
        </nav>
        <div className="crm-header-actions">
          <button
            className="crm-button ghost"
            onClick={() => loadClients()}
            disabled={loading}
          >
            {loading ? 'Syncing…' : 'Refresh'}
          </button>
          <button className="crm-button ghost crm-theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun weight="fill" size={18} /> : <Moon weight="fill" size={18} />}
          </button>
          {user && (
            <div className="crm-user-menu-wrap" ref={userMenuRef}>
              <button
                className="crm-button ghost crm-user-btn"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={userMenuOpen}
              >
                {user.picture && (
                  <img src={user.picture} alt="" className="crm-user-avatar" />
                )}
                <span className="crm-user-name">{user.name?.split(' ')[0] || 'Account'}</span>
                <span className="crm-user-chevron">{userMenuOpen ? <CaretUp weight="fill" size={12} /> : <CaretDown weight="fill" size={12} />}</span>
              </button>
              {userMenuOpen && (
                <div className="crm-user-dropdown">
                  <div className="crm-user-dropdown-header">
                    <span className="crm-user-dropdown-name">{user.name}</span>
                    <span className="crm-user-dropdown-email">{user.email}</span>
                  </div>
                  <div className="crm-user-dropdown-divider" />
                  <button
                    className="crm-user-dropdown-item"
                    onClick={() => { setUserMenuOpen(false); navigate('/dashboard/profile'); }}
                  >
                    <span className="crm-user-dropdown-icon"><User weight="fill" size={14} /></span> Profile
                  </button>
                  <button
                    className="crm-user-dropdown-item crm-user-dropdown-item--danger"
                    onClick={() => { setUserMenuOpen(false); handleSignOut(); }}
                  >
                    <span className="crm-user-dropdown-icon"><SignOut weight="fill" size={14} /></span> Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="crm-main">
        {/* Error Display */}
        {error && <div className="crm-error" style={{ marginBottom: 24 }}>{error}</div>}

        {/* ============================================
            CLIENT LIST VIEW
            ============================================ */}
        {!clientId && (
          <div className="crm-dashboard">
            {/* Left Column: Recent Clients */}
            <div className="crm-dashboard-clients">
              <div className="crm-list-header">
                <div>
                  <h2 className="crm-section-title">Recent clients</h2>
                  <p className="crm-section-subtitle">
                    {totalActiveClients} {totalActiveClients === 1 ? 'client' : 'clients'}
                    {totalActiveClients > 10 && (
                      <button 
                        className="crm-view-all-link"
                        onClick={() => navigate(DASHBOARD_ALL_PATH)}
                      >
                        View all <ArrowRight weight="bold" size={13} style={{ marginLeft: 2, verticalAlign: 'middle' }} />
                      </button>
                    )}
                  </p>
                </div>
                <button className="crm-button primary crm-todos-add-btn" onClick={() => setShowAddModal(true)}>
                  + New
                </button>
              </div>

              <div className="crm-client-list">
                {recentClients.length === 0 && (
                  <div className="crm-empty-state">
                    <div className="crm-empty-icon"><Users weight="fill" size={32} /></div>
                    <p className="crm-empty-title">No clients yet</p>
                    <p className="crm-empty-desc">Add your first client to start tracking relationships.</p>
                    <button className="crm-button primary" onClick={() => setShowAddModal(true)}>+ New client</button>
                  </div>
                )}
                {recentClients.map((client) => (
                  <button
                    key={client.id}
                    className="crm-client-row"
                    data-status={client.status || 'prospect'}
                    onClick={() => navigate(`${DASHBOARD_PATH}/${client.id}`, { state: { from: DASHBOARD_PATH } })}
                  >
                    <ClientAvatar client={client} />
                    <div className="crm-client-info">
                      <p className="crm-client-company">{client.company || 'No company'}</p>
                      <p className="crm-client-contact">{client.name || 'No contact'}{client.email ? ` · ${client.email}` : ''}</p>
                    </div>
                    <div className="crm-client-badges">
                      <span className="crm-chip" data-status={client.status || 'prospect'}>
                        {client.status || 'prospect'}
                      </span>
                      {(client.open_tasks || 0) > 0 && (
                        <span className="crm-task-count">
                          {client.open_tasks} {client.open_tasks === 1 ? 'task' : 'tasks'}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="crm-card-flat crm-dashboard-thoughts">
                <div className="crm-card-head">
                  <h3>Recent thoughts</h3>
                </div>
                {recentThoughtsLoading ? (
                  <div className="crm-thoughts-empty">Loading thoughts...</div>
                ) : recentThoughts.length === 0 ? (
                  <div className="crm-thoughts-empty">
                    No thoughts yet. Add one from a client detail page to see it here.
                  </div>
                ) : (
                  <div className="crm-thought-list">
                    {recentThoughts.map((thought) => (
                      <button
                        key={thought.id}
                        className="crm-thought-row"
                        data-priority={thought.priority || 'casual'}
                        data-size={getThoughtSize(thought.text)}
                        onClick={() => navigate(`${DASHBOARD_PATH}/${thought.client_id}`)}
                      >
                        <div className="crm-thought-row-top">
                          <span className="crm-thought-chip" data-priority={thought.priority || 'casual'}>
                            {getPriorityLabel(thought.priority)}
                          </span>
                          <span className="crm-thought-row-date">
                            {formatThoughtDate(thought.date_thought || thought.created_at)}
                          </span>
                        </div>
                        <p className="crm-thought-row-text">{thought.text}</p>
                        <div className="crm-thought-row-meta">
                          <span className="crm-title">{thought.company || thought.name || 'Client'}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Weekly Tasks */}
            <div className="crm-dashboard-todos">
              <div className="crm-todos-header">
                <div>
                  <h2 className="crm-section-title">Tasks</h2>
                  <p className="crm-section-subtitle">
                    {weeklyTasks.totalTasks} remaining · {weeklyTasks.weekStart}
                  </p>
                  {weeklyTasks.allCount > 0 && (
                    <div className="crm-progress">
                      <div className="crm-progress-track">
                        <div className="crm-progress-fill" style={{ width: `${weeklyTasks.progress}%` }} />
                      </div>
                      <span className="crm-progress-label">
                        {weeklyTasks.completedThisWeek}/{weeklyTasks.allCount} done
                      </span>
                    </div>
                  )}
                </div>
                <div className="crm-todos-header-actions">
                  <button
                    className="crm-button primary crm-todos-add-btn"
                    onClick={() => openAddTaskModal()}
                  >
                    + Add
                  </button>
                  <div className="crm-todos-nav">
                    <button
                      className="crm-button ghost crm-todos-nav-btn"
                      onClick={() => setWeekOffset(prev => prev - 1)}
                      aria-label="Previous week"
                    >
                      ←
                    </button>
                    <button
                      className="crm-button ghost crm-todos-nav-btn"
                      onClick={() => setWeekOffset(0)}
                      disabled={weekOffset === 0}
                    >
                      Today
                    </button>
                    <button
                      className="crm-button ghost crm-todos-nav-btn"
                      onClick={() => setWeekOffset(prev => prev + 1)}
                      aria-label="Next week"
                    >
                      →
                    </button>
                  </div>
                </div>
              </div>

              <div className="crm-todos-week">
                {allTasksLoading ? (
                  <div className="crm-todos-loading">
                    <div className="crm-spinner" />
                    <p>Loading tasks...</p>
                  </div>
                ) : (
                  weeklyTasks.days.map((day) => (
                    <div key={day.date} className="crm-todos-day">
                      <div className="crm-todos-day-header">
                        <span className="crm-todos-day-label">{day.label}</span>
                        <span className="crm-todos-day-date">{day.displayDate}</span>
                        <button
                          className="crm-todos-day-add"
                          onClick={() => openAddTaskModal(day.date)}
                          aria-label={`Add task for ${day.label}`}
                        >
                          +
                        </button>
                      </div>
                      <div className="crm-todos-day-tasks">
                        {day.tasks.length === 0 ? (
                          <p className="crm-todos-empty">No tasks</p>
                        ) : (
                          day.tasks.map((task) => (
                            <div
                              key={task.id}
                              className={`crm-todos-task ${task.completed ? 'completed' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={!!task.completed}
                                onChange={() => toggleTaskFromWeekly(task)}
                                className="crm-task-checkbox"
                              />
                              <div 
                                className="crm-todos-task-content"
                                onClick={() => openEditTaskModal(task)}
                              >
                                <span className="crm-todos-task-title">{task.title}</span>
                                <span 
                                  className="crm-todos-task-client"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`${DASHBOARD_PATH}/${task.clientId}`);
                                  }}
                                >
                                  {task.clientName}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================================
            CLIENT DETAIL VIEW
            ============================================ */}
        {clientId && (
          <div className="crm-detail-wrapper">
            <div className="crm-detail">
              {/* Back Navigation */}
              <div className="crm-detail-nav">
                <button className="crm-button ghost" onClick={() => navigate(backTo)}>
                  ← {backTo === DASHBOARD_ALL_PATH ? 'All clients' : 'Dashboard'}
                </button>
              </div>

              <div className="crm-detail-grid">
                <div className="crm-detail-main">
              {/* Client Info Card */}
              <div className="crm-card-flat crm-client-card">
                <div className="crm-client-header">
                  <div className="crm-client-title-row">
                    <ClientAvatarPicker
                      client={activeClient}
                      onSelect={async (iconName) => {
                        try {
                          const res = await fetch(`/api/crm/clients/${activeClient.id}`, {
                            method: 'PATCH',
                            headers: { ...authHeaders, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ icon: iconName || null })
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setDetail(prev => ({ ...prev, client: data.client }));
                            setClients(prev => prev.map(c => c.id === data.client.id ? data.client : c));
                          }
                        } catch (_) {}
                      }}
                    />
                    {isEditing ? (
                      <input
                        className="crm-input crm-input-name"
                        value={editForm.company || ''}
                        onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                        placeholder="Company name"
                      />
                    ) : (
                      <h2 className="crm-client-name">{activeClient?.company || 'Company'}</h2>
                    )}
                    
                    {isEditing ? (
                      <select
                        className="crm-select-status"
                        value={editForm.status || 'prospect'}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      >
                        {statusOptions.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="crm-chip" data-status={activeClient?.status || 'prospect'}>
                        {activeClient?.status || 'prospect'}
                      </span>
                    )}
                  </div>
                  
                  <div className="crm-client-actions">
                    {isEditing ? (
                      <>
                        <button className="crm-button ghost" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                        <button className="crm-button primary" onClick={handleSaveClient} disabled={loading}>
                          {loading ? 'Saving…' : 'Save'}
                        </button>
                      </>
                    ) : (
                      <button className="crm-button" onClick={() => setIsEditing(true)}>
                        Edit
                      </button>
                    )}
                  </div>
                </div>

                {/* Client Info Row */}
                <div className="crm-client-info-row">
                  {(isEditing || activeClient?.name) && (
                    <div className="crm-info-field">
                      <span className="crm-info-label">Contact</span>
                      {isEditing ? (
                        <input
                          className="crm-input crm-input-inline"
                          value={editForm.name || ''}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          placeholder="—"
                        />
                      ) : (
                        <span className="crm-info-value">{activeClient?.name}</span>
                      )}
                    </div>
                  )}
                  
                  {(isEditing || activeClient?.phone) && (
                    <>
                      {(isEditing || activeClient?.name) && <div className="crm-info-divider" />}
                      <div className="crm-info-field">
                        <span className="crm-info-label">Phone</span>
                        {isEditing ? (
                          <input
                            className="crm-input crm-input-inline"
                            value={editForm.phone || ''}
                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            placeholder="—"
                          />
                        ) : (
                          <span className="crm-info-value">
                            <a href={`tel:${activeClient.phone}`} className="crm-link">
                              {activeClient.phone}
                            </a>
                          </span>
                        )}
                      </div>
                    </>
                  )}
                  
                  {(isEditing || activeClient?.source) && (
                    <>
                      {(isEditing || activeClient?.name || activeClient?.phone) && <div className="crm-info-divider" />}
                      <div className="crm-info-field">
                        <span className="crm-info-label">Source</span>
                        {isEditing ? (
                          <input
                            className="crm-input crm-input-inline"
                            value={editForm.source || ''}
                            onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                            placeholder="—"
                          />
                        ) : (
                          <span className="crm-info-value">{activeClient?.source}</span>
                        )}
                      </div>
                    </>
                  )}
                  
                  {(isEditing || activeClient?.lifetime_value) && (
                    <>
                      {(isEditing || activeClient?.name || activeClient?.phone || activeClient?.source) && <div className="crm-info-divider" />}
                      <div className="crm-info-field">
                        <span className="crm-info-label">Lifetime Value</span>
                        {isEditing ? (
                          <div className="crm-currency-input">
                            <span className="crm-currency-symbol">$</span>
                            <input
                              className="crm-input crm-input-inline crm-input-currency"
                              type="number"
                              step="0.01"
                              min="0"
                              value={editForm.lifetime_value || ''}
                              onChange={(e) => setEditForm({ ...editForm, lifetime_value: e.target.value ? parseFloat(e.target.value) : 0 })}
                              placeholder="0"
                            />
                          </div>
                        ) : (
                          <span className="crm-info-value crm-value-money">
                            ${Number(activeClient.lifetime_value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Email Section - only show if editing or has emails */}
                {(isEditing || allClientEmails.length > 0) && (
                  <div className="crm-client-emails">
                    <span className="crm-info-label">Emails</span>
                    {isEditing ? (
                      <div className="crm-emails-edit">
                        <input
                          className="crm-input crm-input-inline"
                          type="email"
                          value={editForm.email || ''}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          placeholder="Primary email"
                        />
                        {(editForm.additional_emails || []).map((email, index) => (
                          <div key={index} className="crm-email-row">
                            <input
                              className="crm-input crm-input-inline"
                              type="email"
                              value={email}
                              onChange={(e) => handleUpdateAdditionalEmail(index, e.target.value)}
                              placeholder="Additional email"
                            />
                            <button
                              type="button"
                              className="crm-email-remove"
                              onClick={() => handleRemoveAdditionalEmail(index)}
                              aria-label="Remove email"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="crm-button ghost crm-add-email-btn"
                          onClick={handleAddAdditionalEmail}
                        >
                          + Add email
                        </button>
                      </div>
                    ) : (
                      <div className="crm-emails-display">
                        <div className="crm-email-tags">
                          {allClientEmails.map((email, index) => (
                            <a 
                              key={index} 
                              href={`mailto:${email}`} 
                              className="crm-email-tag"
                            >
                              {email}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Slack Channels Section - only show if editing or has channels */}
                {(isEditing || (activeClient?.slack_channels || []).length > 0) && (
                  <div className="crm-client-slack">
                    <span className="crm-info-label">Slack Channels</span>
                    {isEditing ? (
                      <div className="crm-slack-edit">
                        {(editForm.slack_channels || []).map((channel, index) => (
                          <div key={index} className="crm-slack-row">
                            <input
                              className="crm-input crm-input-inline crm-slack-name-input"
                              value={channel.name}
                              onChange={(e) => handleUpdateSlackChannel(index, 'name', e.target.value)}
                              placeholder="channel-name"
                            />
                            <input
                              className="crm-input crm-input-inline crm-slack-url-input"
                              type="url"
                              value={channel.url}
                              onChange={(e) => handleUpdateSlackChannel(index, 'url', e.target.value)}
                              placeholder="https://yourworkspace.slack.com/channels/..."
                            />
                            <button
                              type="button"
                              className="crm-email-remove"
                              onClick={() => handleRemoveSlackChannel(index)}
                              aria-label="Remove Slack channel"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="crm-button ghost crm-add-email-btn"
                          onClick={handleAddSlackChannel}
                        >
                          + Add channel
                        </button>
                      </div>
                    ) : (
                      <div className="crm-emails-display">
                        <div className="crm-email-tags">
                          {(activeClient?.slack_channels || []).map((channel, index) => (
                            <a
                              key={index}
                              href={channel.url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="crm-email-tag crm-slack-tag"
                            >
                              #{channel.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Email Preview Section */}
                <div className="crm-client-email-preview">
                  <div className="crm-email-preview-header">
                    <span className="crm-info-label">Recent Email</span>
                    {emailConnection.connected && emailThreads.length > 0 && (
                      <button 
                        className="crm-link crm-view-all"
                        onClick={() => setShowEmailHistory(true)}
                      >
                        View all
                      </button>
                    )}
                  </div>
                  
                  {!emailConnection.connected ? (
                    <div className="crm-email-preview-connect">
                      <span className="crm-muted">Connect Gmail to see email history</span>
                      <button 
                        className="crm-button ghost"
                        onClick={() => setShowEmailHistory(true)}
                      >
                        Connect
                      </button>
                    </div>
                  ) : emailsLoading && emailThreads.length === 0 ? (
                    <div className="crm-email-preview-loading">
                      <span className="crm-muted">Loading...</span>
                    </div>
                  ) : latestEmailThread ? (
                    <div 
                      className="crm-email-preview-item"
                      onClick={() => setShowEmailHistory(true)}
                    >
                      <div className="crm-email-preview-row">
                        <span className="crm-email-preview-from">
                          {latestEmailThread.latestMessage?.from?.name || latestEmailThread.latestMessage?.from?.email}
                        </span>
                        <span className="crm-email-preview-date">
                          {formatEmailDate(latestEmailThread.latestMessage?.timestamp)}
                        </span>
                      </div>
                      <div className="crm-email-preview-subject">
                        {latestEmailThread.latestMessage?.subject}
                        {latestEmailThread.messageCount > 1 && (
                          <span className="crm-email-thread-count">
                            ({latestEmailThread.messageCount})
                          </span>
                        )}
                      </div>
                      <div className="crm-email-preview-snippet">
                        {latestEmailThread.latestMessage?.snippet}
                      </div>
                    </div>
                  ) : (
                    <div className="crm-email-preview-empty">
                      <span className="crm-muted">No emails with this client</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Thoughts Section - Like Docs, no container */}
              <div className="crm-thoughts-section">
                <div className="crm-thoughts-header">
                  <div className="crm-thoughts-header-left">
                    <h3 className="crm-thoughts-title">Thoughts</h3>
                    <span className="crm-thoughts-count">{thoughts.length} total</span>
                  </div>
                  <div className="crm-thoughts-header-actions">
                    <button
                      className="crm-button primary"
                      onClick={openAddThoughtModal}
                    >
                      + Add Thought
                    </button>
                  </div>
                </div>

                {thoughts.length === 0 ? (
                  <div className="crm-thoughts-empty">
                    <p className="crm-muted">No thoughts yet. Add quick notes to capture context.</p>
                  </div>
                ) : (
                  <div className="crm-thought-grid">
                    {thoughts.map((thought) => (
                      <div 
                        key={thought.id} 
                        className="crm-thought-card" 
                        data-priority={thought.priority || 'casual'}
                        data-size={getThoughtSize(thought.text)}
                        onClick={() => openEditThoughtModal(thought)}
                      >
                        <div className="crm-thought-card-top">
                          <span className="crm-thought-priority">{getPriorityLabel(thought.priority)}</span>
                          <button
                            type="button"
                            className="crm-thought-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteThought(thought.id);
                            }}
                            aria-label="Delete thought"
                          >
                            <Trash weight="fill" size={14} />
                          </button>
                        </div>
                        <p className="crm-thought-text">{thought.text}</p>
                        <div className="crm-thought-meta">
                          <span className="crm-thought-date">
                            {formatThoughtDate(thought.date_thought || thought.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Docs Section */}
              <div className="crm-docs-section">
                <div className="crm-docs-header">
                  <div className="crm-docs-header-left">
                    <h3 className="crm-docs-title">Docs</h3>
                    <span className="crm-docs-count">{docsPagination.total} total</span>
                  </div>
                  <div className="crm-docs-header-actions">
                    <button
                      className="crm-button"
                      onClick={() => setShowAddDocModal(true)}
                    >
                      + Add Doc
                    </button>
                    <div className="crm-new-doc-dropdown">
                      <button
                        className="crm-button primary"
                        onClick={() => openNewGoogleDoc('document')}
                      >
                        New Doc ↗
                      </button>
                    </div>
                  </div>
                </div>

                {/* Docs Grid */}
                {docsLoading && docs.length === 0 ? (
                  <p className="crm-muted" style={{ padding: '24px 0' }}>Loading docs…</p>
                ) : docs.length === 0 ? (
                  <div className="crm-docs-empty">
                    <p className="crm-muted">No docs yet. Add a Google Doc URL or create a new one.</p>
                  </div>
                ) : (
                  <>
                    <div className="crm-docs-grid">
                      {docs.map((doc) => {
                        const typeInfo = getDocTypeInfo(doc.doc_type);
                        return (
                          <a
                            key={doc.id}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="crm-doc-card"
                          >
                            <div 
                              className="crm-doc-preview"
                              style={{ '--doc-color': typeInfo.color }}
                            >
                              {doc.thumbnail_url ? (
                                <img 
                                  src={doc.thumbnail_url} 
                                  alt={doc.title}
                                  className="crm-doc-thumbnail"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className="crm-doc-icon-fallback"
                                style={{ display: doc.thumbnail_url ? 'none' : 'flex' }}
                              >
                                <span className="crm-doc-icon">{typeInfo.icon}</span>
                              </div>
                              <span className="crm-doc-type-badge">{typeInfo.label}</span>
                            </div>
                            <div className="crm-doc-info">
                              <p className="crm-doc-title">{doc.title}</p>
                            </div>
                            <button
                              className="crm-doc-delete"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (window.confirm('Remove this document?')) {
                                  handleDeleteDoc(doc.id);
                                }
                              }}
                              aria-label="Delete document"
                            >
                              ×
                            </button>
                          </a>
                        );
                      })}
                    </div>

                    {/* Pagination */}
                    {docsPagination.totalPages > 1 && (
                      <div className="crm-docs-pagination">
                        <button
                          className="crm-button ghost"
                          onClick={() => loadDocs(detail.client.id, docsPage - 1)}
                          disabled={docsPage === 1 || docsLoading}
                        >
                          ← Previous
                        </button>
                        <span className="crm-docs-page-info">
                          Page {docsPage} of {docsPagination.totalPages}
                        </span>
                        <button
                          className="crm-button ghost"
                          onClick={() => loadDocs(detail.client.id, docsPage + 1)}
                          disabled={!docsPagination.hasMore || docsLoading}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <aside className="crm-detail-sidebar">
              {/* ── What's Next ── */}
              <div className="crm-card-flat crm-whats-next-card">
                <div className="crm-whats-next-header">
                  <h3 className="crm-tasks-title">What's Next</h3>
                </div>

                {activeMilestone && !showMilestoneForm ? (
                  <div className="crm-whats-next-active">
                    <p className="crm-whats-next-text">{activeMilestone.text}</p>
                    <span className="crm-whats-next-date">
                      Added {new Date(activeMilestone.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <div className="crm-whats-next-actions">
                      <button
                        className="crm-button primary"
                        onClick={handleCompleteMilestone}
                        disabled={milestoneLoading}
                        style={{ fontSize: 12, padding: '5px 12px' }}
                      >
                        {milestoneLoading ? '…' : 'Update Next'}
                      </button>
                      <button
                        className="crm-button ghost"
                        onClick={handleDismissMilestone}
                        disabled={milestoneLoading}
                        style={{ fontSize: 12, padding: '5px 10px' }}
                      >
                        Nothing for now
                      </button>
                    </div>
                  </div>
                ) : showMilestoneForm || !activeMilestone ? (
                  <form className="crm-whats-next-form" onSubmit={handleAddMilestone}>
                    <input
                      className="crm-input"
                      placeholder="What's the next milestone?"
                      value={milestoneInput}
                      onChange={(e) => setMilestoneInput(e.target.value)}
                      autoFocus
                    />
                    <div className="crm-whats-next-form-actions">
                      <button
                        type="submit"
                        className="crm-button primary"
                        disabled={milestoneLoading || !milestoneInput.trim()}
                        style={{ fontSize: 12, padding: '5px 12px' }}
                      >
                        {milestoneLoading ? '…' : 'Set Milestone'}
                      </button>
                      {showMilestoneForm && (
                        <button
                          type="button"
                          className="crm-button ghost"
                          onClick={() => { setShowMilestoneForm(false); setMilestoneInput(''); }}
                          style={{ fontSize: 12, padding: '5px 10px' }}
                        >
                          Nothing for now
                        </button>
                      )}
                    </div>
                  </form>
                ) : null}

                {milestoneHistory.length > 0 && (
                  <div className="crm-whats-next-history">
                    <button
                      type="button"
                      className="crm-tasks-completed-toggle"
                      onClick={() => setMilestoneHistoryExpanded(!milestoneHistoryExpanded)}
                    >
                      <span className="crm-tasks-completed-label">{milestoneHistory.length} past</span>
                      <span className={`crm-tasks-toggle-icon ${milestoneHistoryExpanded ? 'expanded' : ''}`}><CaretDown weight="fill" size={12} /></span>
                    </button>
                    {milestoneHistoryExpanded && (
                      <div className="crm-whats-next-history-list">
                        {milestoneHistory.map((m) => (
                          <div key={m.id} className="crm-whats-next-history-item">
                            <span className={`crm-whats-next-history-status ${m.status}`}>
                              {m.status === 'done' ? '✓' : '—'}
                            </span>
                            <div className="crm-whats-next-history-body">
                              <span className="crm-whats-next-history-text">{m.text}</span>
                              <span className="crm-whats-next-history-date">
                                {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {m.completed_at && ` → ${new Date(m.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="crm-card-flat crm-tasks-card">
                <div className="crm-tasks-header">
                  <h3 className="crm-tasks-title">Tasks</h3>
                  <span className="crm-tasks-count">{openTasks.length} open</span>
                </div>

                <form className="crm-task-add-v2" onSubmit={handleAddTask}>
                  <div className="crm-task-add-row">
                    <input
                      className="crm-input crm-task-input-large"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                      placeholder="What needs to be done?"
                    />
                  </div>
                  <div className="crm-task-add-meta">
                    <input
                      className="crm-date-input"
                      type="date"
                      value={taskForm.due_date}
                      onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                    />
                    <div className="crm-task-quick-dates">
                      <button type="button" className="crm-quick-date" onClick={() => setTaskForm({ ...taskForm, due_date: new Date().toISOString().split('T')[0] })}>Today</button>
                      <button type="button" className="crm-quick-date" onClick={() => {
                        const d = new Date(); d.setDate(d.getDate() + 1);
                        setTaskForm({ ...taskForm, due_date: d.toISOString().split('T')[0] });
                      }}>Tmrw</button>
                    </div>
                    <button 
                      className="crm-button primary crm-task-add-btn" 
                      type="submit" 
                      disabled={taskLoading || !taskForm.title.trim()}
                    >
                      {taskLoading ? '…' : 'Add Task'}
                    </button>
                  </div>
                </form>

                <div className="crm-task-list">
                  {taskLoading && openTasks.length === 0 && (
                    <p className="crm-muted" style={{ padding: '16px 0' }}>Loading tasks...</p>
                  )}
                  
                  {openTasks.map((task) => (
                    <div key={task.id} className="crm-task-item">
                      <input
                        type="checkbox"
                        checked={!!task.completed}
                        onChange={() => toggleTask(task)}
                        className="crm-task-checkbox"
                      />
                      <div 
                        className="crm-task-content crm-task-clickable"
                        onClick={() => openEditTaskModal({ ...task, clientId: detail?.client?.id })}
                      >
                        <span className="crm-task-title">{task.title}</span>
                        {task.due_date && (
                          <span className="crm-task-due">{task.due_date}</span>
                        )}
                      </div>
                    </div>
                  ))}

                  {completedTasks.length > 0 && (
                    <div className="crm-tasks-completed">
                      <button 
                        className="crm-tasks-completed-toggle"
                        onClick={() => setCompletedTasksCollapsed(!completedTasksCollapsed)}
                        type="button"
                      >
                        <span className="crm-tasks-completed-label">
                          {completedTasks.length} completed
                        </span>
                        <span className={`crm-tasks-toggle-icon ${completedTasksCollapsed ? '' : 'expanded'}`}>
                          <CaretDown weight="fill" size={12} />
                        </span>
                      </button>
                      {!completedTasksCollapsed && (
                        <div className="crm-tasks-completed-list">
                          {completedTasks.map((task) => (
                            <div key={task.id} className="crm-task-item completed">
                              <input
                                type="checkbox"
                                checked={!!task.completed}
                                onChange={() => toggleTask(task)}
                                className="crm-task-checkbox"
                              />
                              <div 
                                className="crm-task-content crm-task-clickable"
                                onClick={() => openEditTaskModal({ ...task, clientId: detail?.client?.id })}
                              >
                                <span className="crm-task-title">{task.title}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {!taskLoading && openTasks.length === 0 && completedTasks.length === 0 && (
                    <p className="crm-muted" style={{ padding: '24px 0' }}>
                      No tasks yet. Add one above.
                    </p>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
        </div>
        )}
      </main>

      {/* ============================================
          ADD CLIENT MODAL
          ============================================ */}
      {showAddModal && (
        <div className="crm-modal-backdrop" onClick={() => setShowAddModal(false)}>
          <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-card-head">
              <h3>New client</h3>
              <button className="crm-button ghost" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
            </div>
            <form className="crm-form" onSubmit={handleCreateClient}>
              <input
                className="crm-input"
                placeholder="Company *"
                value={clientForm.company}
                onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })}
                autoFocus
              />
              <input
                className="crm-input"
                placeholder="Contact name"
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
              />
              <div className="crm-row-split">
                <input
                  className="crm-input"
                  placeholder="Email"
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                />
                <input
                  className="crm-input"
                  placeholder="Phone"
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                />
              </div>
              <div className="crm-row-split">
                <select
                  className="crm-select-min"
                  value={clientForm.status}
                  onChange={(e) => setClientForm({ ...clientForm, status: e.target.value })}
                >
                  {statusOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <input
                  className="crm-input"
                  placeholder="Source"
                  value={clientForm.source}
                  onChange={(e) => setClientForm({ ...clientForm, source: e.target.value })}
                />
              </div>
              <div className="crm-modal-actions">
                <button
                  className="crm-button ghost"
                  type="button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button className="crm-button primary" type="submit" disabled={loading}>
                  {loading ? 'Creating…' : 'Create client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================
          ADD DOC MODAL
          ============================================ */}
      {showAddDocModal && (
        <div className="crm-modal-backdrop" onClick={() => setShowAddDocModal(false)}>
          <div className="crm-modal crm-modal-doc" onClick={(e) => e.stopPropagation()}>
            <div className="crm-card-head">
              <h3>Add Google Doc</h3>
              <button className="crm-button ghost" onClick={() => setShowAddDocModal(false)}>
                Cancel
              </button>
            </div>
            <form className="crm-form" onSubmit={handleAddDoc}>
              <div className="crm-doc-form-help">
                <p>Paste a Google Docs, Sheets, Slides, or Forms URL:</p>
              </div>
              <input
                className="crm-input"
                placeholder="https://docs.google.com/document/d/..."
                value={docForm.url}
                onChange={(e) => setDocForm({ ...docForm, url: e.target.value })}
                autoFocus
              />
              <input
                className="crm-input"
                placeholder="Document title (optional)"
                value={docForm.title}
                onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
              />
              {error && <div className="crm-error">{error}</div>}
              <div className="crm-modal-actions">
                <button
                  className="crm-button ghost"
                  type="button"
                  onClick={() => {
                    setShowAddDocModal(false);
                    setDocForm(emptyDocForm);
                    setError('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="crm-button primary" 
                  type="submit" 
                  disabled={docsLoading || !docForm.url.trim()}
                >
                  {docsLoading ? 'Adding…' : 'Add Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================
          TASK EDIT/ADD MODAL
          ============================================ */}
      {showTaskModal && (
        <div className="crm-modal-backdrop" onClick={() => setShowTaskModal(false)}>
          <div className="crm-modal crm-modal-task" onClick={(e) => e.stopPropagation()}>
            <div className="crm-card-head">
              <h3>{editingTask ? 'Edit Task' : 'New Task'}</h3>
              <button className="crm-button ghost" onClick={() => setShowTaskModal(false)}>
                Cancel
              </button>
            </div>
            <form className="crm-form" onSubmit={handleSaveTaskModal}>
              <input
                className="crm-input crm-task-input-large"
                placeholder="What needs to be done?"
                value={taskModalForm.title}
                onChange={(e) => setTaskModalForm({ ...taskModalForm, title: e.target.value })}
                autoFocus
              />
              <div className="crm-task-modal-meta">
                <div className="crm-form-field">
                  <label className="crm-form-label">Due Date</label>
                  <input
                    className="crm-input"
                    type="date"
                    value={taskModalForm.due_date}
                    onChange={(e) => setTaskModalForm({ ...taskModalForm, due_date: e.target.value })}
                  />
                  <div className="crm-task-quick-dates">
                    <button type="button" className="crm-quick-date" onClick={() => setTaskModalForm({ ...taskModalForm, due_date: new Date().toISOString().split('T')[0] })}>Today</button>
                    <button type="button" className="crm-quick-date" onClick={() => {
                      const d = new Date(); d.setDate(d.getDate() + 1);
                      setTaskModalForm({ ...taskModalForm, due_date: d.toISOString().split('T')[0] });
                    }}>Tomorrow</button>
                    <button type="button" className="crm-quick-date" onClick={() => {
                      const d = new Date(); const dow = d.getDay(); const diff = dow === 0 ? 1 : 8 - dow;
                      d.setDate(d.getDate() + diff);
                      setTaskModalForm({ ...taskModalForm, due_date: d.toISOString().split('T')[0] });
                    }}>Next Mon</button>
                  </div>
                </div>
                {!editingTask && (
                  <div className="crm-form-field">
                    <label className="crm-form-label">Client *</label>
                    <select
                      className="crm-select-min"
                      value={taskModalForm.client_id}
                      onChange={(e) => setTaskModalForm({ ...taskModalForm, client_id: e.target.value })}
                    >
                      <option value="">Select client...</option>
                      {clients.filter(c => c.status !== 'archived').map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.company || c.name || 'Unknown'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {editingTask && (
                  <div className="crm-form-field">
                    <label className="crm-form-label">Client</label>
                    <span className="crm-form-value">
                      {clients.find(c => c.id === (editingTask.client_id || editingTask.clientId))?.company || 
                       clients.find(c => c.id === (editingTask.client_id || editingTask.clientId))?.name || 
                       'Unknown'}
                    </span>
                  </div>
                )}
              </div>
              {error && <div className="crm-error">{error}</div>}
              <div className="crm-modal-actions">
                {editingTask && (
                  <button
                    className="crm-button crm-button-danger"
                    type="button"
                    onClick={() => handleDeleteTask(editingTask)}
                  >
                    Delete
                  </button>
                )}
                <div className="crm-modal-actions-right">
                  <button
                    className="crm-button ghost"
                    type="button"
                    onClick={() => setShowTaskModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="crm-button primary" 
                    type="submit" 
                    disabled={taskLoading || !taskModalForm.title.trim() || (!editingTask && !taskModalForm.client_id)}
                  >
                    {taskLoading ? 'Saving…' : (editingTask ? 'Save' : 'Add Task')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================
          THOUGHT MODAL (Add/Edit)
          ============================================ */}
      {showThoughtModal && (
        <div className="crm-modal-backdrop" onClick={() => setShowThoughtModal(false)}>
          <div className="crm-modal crm-modal-thought" onClick={(e) => e.stopPropagation()}>
            <div className="crm-card-head">
              <h3>{editingThought ? 'Edit Thought' : 'New Thought'}</h3>
              <button className="crm-button ghost" onClick={() => setShowThoughtModal(false)}>
                Cancel
              </button>
            </div>
            <form className="crm-thought-modal-form" onSubmit={handleSaveThought}>
              <textarea
                className="crm-textarea-min"
                placeholder="What's on your mind about this client..."
                value={thoughtForm.text}
                onChange={(e) => setThoughtForm({ ...thoughtForm, text: e.target.value })}
                autoFocus
              />
              <div className="crm-thought-modal-meta">
                <select
                  className="crm-select-min"
                  value={thoughtForm.priority}
                  onChange={(e) => setThoughtForm({ ...thoughtForm, priority: e.target.value })}
                >
                  {thoughtPriorityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input
                  className="crm-date-input"
                  type="date"
                  value={thoughtForm.date_thought}
                  onChange={(e) => setThoughtForm({ ...thoughtForm, date_thought: e.target.value })}
                />
              </div>
              {error && <div className="crm-error">{error}</div>}
              <div className="crm-modal-actions">
                {editingThought && (
                  <button
                    className="crm-button crm-button-danger"
                    type="button"
                    onClick={() => {
                      handleDeleteThought(editingThought.id, true);
                      setShowThoughtModal(false);
                      setEditingThought(null);
                    }}
                  >
                    Delete
                  </button>
                )}
                <div className="crm-modal-actions-right">
                  <button
                    className="crm-button ghost"
                    type="button"
                    onClick={() => setShowThoughtModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="crm-button primary" 
                    type="submit" 
                    disabled={thoughtLoading || !thoughtForm.text.trim()}
                  >
                    {thoughtLoading ? 'Saving…' : (editingThought ? 'Save' : 'Add Thought')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================
          EMAIL HISTORY POPUP
          ============================================ */}
      {showEmailHistory && (
        <div className="crm-modal-backdrop" onClick={closeEmailHistory}>
          <div className="crm-modal crm-modal-email" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="crm-email-header">
              <div className="crm-email-header-left">
                {selectedMessage ? (
                  <button className="crm-email-back" onClick={backToThread}>
                    ← Back
                  </button>
                ) : selectedThread ? (
                  <button className="crm-email-back" onClick={backToList}>
                    ← Back
                  </button>
                ) : (
                  <h3>Email History</h3>
                )}
                {!selectedThread && !selectedMessage && emailConnection.connected && emailConnection.gmailEmail && (
                  <span className="crm-email-connected-badge">
                    <span className="crm-email-connected-dot" />
                    {emailConnection.gmailEmail}
                  </span>
                )}
              </div>
              <button 
                className="crm-modal-close" 
                onClick={closeEmailHistory}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="crm-email-content">
              {/* Loading state */}
              {emailsLoading && emailThreads.length === 0 && !selectedThread && !selectedMessage && (
                <div className="crm-email-loading">
                  <div className="crm-email-spinner" />
                  <p>Loading emails…</p>
                </div>
              )}

              {/* Not connected state */}
              {!emailsLoading && !emailConnection.connected && !selectedThread && !selectedMessage && (
                <div className="crm-email-connect">
                  <div className="crm-email-connect-icon">
                    <Envelope weight="fill" size={48} />
                  </div>
                  <h4>Connect Gmail</h4>
                  <p>
                    {emailConnection.expired 
                      ? 'Your Gmail access has expired. Please reconnect to continue viewing email history.'
                      : 'Connect your Gmail account to view email history with this client.'
                    }
                  </p>
                  {emailConnection.clientEmails.length > 0 && (
                    <p className="crm-email-client-hint">
                      Tracking emails with: <strong>{emailConnection.clientEmails.join(', ')}</strong>
                    </p>
                  )}
                  <button className="crm-button primary" onClick={connectGmail}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8 }}>
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Connect Gmail
                  </button>
                </div>
              )}

              {/* Full Message View */}
              {selectedMessage && (
                <div className="crm-email-message-view">
                  {messageLoading ? (
                    <div className="crm-email-loading">
                      <div className="crm-email-spinner" />
                      <p>Loading message…</p>
                    </div>
                  ) : (
                    <>
                      <div className="crm-email-message-header">
                        <h4 className="crm-email-message-subject">{selectedMessage.subject}</h4>
                        <div className="crm-email-message-meta">
                          <span className="crm-email-message-from">
                            From: {selectedMessage.from?.name || selectedMessage.from?.email}
                            {selectedMessage.from?.name && <span className="crm-email-meta-email"> &lt;{selectedMessage.from.email}&gt;</span>}
                          </span>
                          <span className="crm-email-message-to">
                            To: {selectedMessage.to?.name || selectedMessage.to?.email}
                          </span>
                          <span className="crm-email-message-date">
                            {formatFullDate(selectedMessage.timestamp)}
                          </span>
                        </div>
                      </div>
                      <div className="crm-email-message-body">
                        {selectedMessage.body || selectedMessage.snippet}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Thread View */}
              {selectedThread && !selectedMessage && (
                <div className="crm-email-thread-view">
                  {threadLoading ? (
                    <div className="crm-email-loading">
                      <div className="crm-email-spinner" />
                      <p>Loading thread…</p>
                    </div>
                  ) : (
                    <>
                      <div className="crm-email-thread-header">
                        <h4>{selectedThread.subject}</h4>
                        <span className="crm-email-thread-meta">
                          {selectedThread.messageCount} messages in this thread
                        </span>
                      </div>
                      <div className="crm-email-thread-messages">
                        {selectedThread.messages?.map((msg, index) => (
                          <div 
                            key={msg.id} 
                            className="crm-email-thread-message"
                            onClick={() => loadMessage(msg.id)}
                          >
                            <div className="crm-email-thread-message-header">
                              <span className="crm-email-thread-from">
                                {msg.from?.name || msg.from?.email}
                              </span>
                              <span className="crm-email-thread-date">
                                {formatEmailDate(msg.timestamp)}
                              </span>
                            </div>
                            <div className="crm-email-thread-snippet">
                              {msg.snippet}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Thread List View */}
              {emailConnection.connected && !selectedThread && !selectedMessage && (
                <>
                  {emailThreads.length === 0 && !emailsLoading ? (
                    <div className="crm-email-empty">
                      <p>No emails found with {emailConnection.clientEmails.join(' or ')}.</p>
                    </div>
                  ) : (
                    <div className="crm-email-list">
                      {emailThreads.map((thread) => (
                        <div 
                          key={thread.threadId} 
                          className={`crm-email-item ${thread.latestMessage?.isUnread ? 'unread' : ''}`}
                          onClick={() => loadThread(thread.threadId)}
                        >
                          <div className="crm-email-item-header">
                            <span className="crm-email-from">
                              {thread.latestMessage?.from?.name || thread.latestMessage?.from?.email}
                            </span>
                            <div className="crm-email-item-right">
                              {thread.messageCount > 1 && (
                                <span className="crm-email-count">{thread.messageCount}</span>
                              )}
                              <span className="crm-email-date">
                                {formatEmailDate(thread.latestMessage?.timestamp)}
                              </span>
                            </div>
                          </div>
                          <div className="crm-email-subject">{thread.latestMessage?.subject}</div>
                          <div className="crm-email-snippet">{thread.latestMessage?.snippet}</div>
                        </div>
                      ))}

                      {/* Load more button */}
                      {emailNextPageToken && (
                        <button
                          className="crm-button ghost crm-email-load-more"
                          onClick={() => loadEmailHistory(detail.client.id, emailNextPageToken)}
                          disabled={emailsLoading}
                        >
                          {emailsLoading ? 'Loading…' : 'Load more emails'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Footer with disconnect option */}
                  <div className="crm-email-footer">
                    <button
                      className="crm-button ghost crm-email-disconnect"
                      onClick={disconnectGmail}
                    >
                      Disconnect Gmail
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Crm;
